import {
  RecoClickEvent,
  RecoEventContext,
  RecoItemData,
  RecoViewEvent,
  AbTestEvent
} from "@cube/tracking-library-api";
import RecoTracking from "../RecoTracking";
import {
  CarouselTrackingData,
  GiftcardTrackingData,
  RecoItemTrackingData,
  ProductTrackingData,
} from "../CarouselTrackingData";

export const layer = "layer" as const;
export const bottom = "bottom" as const;
export const addToCartLayer = "add_to_cart_page.1" as const;

const getPlacementPosition: (p: string) => "layer" | "bottom" = (p) =>
  p === addToCartLayer ? layer : bottom;

const getEventContext: (p: string) => RecoEventContext = (p) =>
  p === addToCartLayer ? { event_context: "a2c" } : {};

export default class CubeRecoTracking extends RecoTracking {
  trackRecoClick(data: CarouselTrackingData, position: string): void {
    const item = data.recoItems.filter((p) => p.position === position);

    const recoEvent: RecoClickEvent = {
      event_name: "reco_click",
      responsible_team_tracking: "RECO",
      responsible_team_feature: "RECO",
      currency: {
        code: data.currency,
      },
      event_data: {
        is_interaction_event: true,
        ...getEventContext(data.placement),
      },
      items: item
        .map(CubeRecoTracking.produktTrackingDataToRecoItemData)
        .filter((data): data is RecoItemData => data !== null),
      promotion_data: {
        type: "slider",
        placement: getPlacementPosition(data.placement),
        name: data.headline,
        rule: data.strategy,
      },
    };
    window.CubeDataLayer?.push(recoEvent);
  }

  trackRecoImpression(data: CarouselTrackingData): void {
    const cubeRecoEvent: RecoViewEvent = {
      event_name: "reco_view",
      responsible_team_tracking: "RECO",
      responsible_team_feature: "RECO",
      currency: {
        code: data.currency,
      },
      event_data: {
        is_interaction_event: false,
        ...getEventContext(data.placement),
      },
      items: data.recoItems
        .map(CubeRecoTracking.produktTrackingDataToRecoItemData)
        .filter((data): data is RecoItemData => data !== null),
      promotion_data: {
        type: "slider",
        placement: getPlacementPosition(data.placement),
        name: data.headline,
        rule: data.strategy,
      },
    };
    window.CubeDataLayer?.push(cubeRecoEvent);
  }

  private static recoItemTrackingDataToRecoItemData(
    produktTrackingData: ProductTrackingData,
  ): RecoItemData | null {
    const position = Number.parseInt(produktTrackingData.position, 10) + 1;
    const urlSearchParams = new URLSearchParams();
    urlSearchParams.append("variant", `${produktTrackingData.farbId}`);
    const targetUrl = new URL(
      produktTrackingData.produktLink,
      window.location.origin,
    );
    targetUrl.search = urlSearchParams.toString();

    if (!Number.isNaN(position)) {
      return {
        item_type: "Product",
        is_sponsored: false,
        target_url: targetUrl.toString(),
        position: position,
        syan: produktTrackingData.syan,
        farb_id: produktTrackingData.farbId,
      };
    } else {
      return null;
    }
  }

  private static giftcardTrackingDataToRecoItemData(
    produktTrackingData: GiftcardTrackingData,
  ): RecoItemData | null {
    const position = Number.parseInt(produktTrackingData.position, 10) + 1;
    const targetUrl = new URL(
      produktTrackingData.produktLink,
      window.location.origin,
    );
    if (!Number.isNaN(position)) {
      return {
        item_type: "Giftcard",
        is_sponsored: false,
        target_url: targetUrl.toString(),
        position: position,
      };
    } else {
      return null;
    }
  }

  trackABTest = (abTestVariante: string | null | undefined, abTestHeader: string | null | undefined): void => {
    if (abTestVariante && abTestHeader) {
      const event: AbTestEvent = {
        event_name: "ab_test",
        responsible_team_feature: "RECO",
        responsible_team_tracking: "RECO",
        event_data: {
          event_category: "site_variation",
          event_type: "test",
          is_interaction_event: false,
          event_context: abTestHeader,
          event_content: [abTestVariante],
        },
      };
      window.CubeDataLayer?.push(event);
    }
  };

  static produktTrackingDataToRecoItemData(
    produktTrackingData: RecoItemTrackingData,
  ): RecoItemData | null {
    switch (produktTrackingData.itemType) {
      case "Product":
        return CubeRecoTracking.recoItemTrackingDataToRecoItemData(
          produktTrackingData,
        );
      case "Giftcard":
        return CubeRecoTracking.giftcardTrackingDataToRecoItemData(
          produktTrackingData,
        );
    }
  }
}
